import { CourseAnswerComparisons } from './course-answer-comparisons';
import { useContext } from "react";
import { formatCoursePrice } from '../../general/currency';
import { CourseManagementDataContext, CourseManagementDataContextValue } from "../../providers/course-management-provider";
import { Panel } from "primereact/panel";
import { Tag } from "primereact/tag";
import { Divider } from "primereact/divider";
import { formatUserName } from "../../general/user";
import { UserDto } from "../../general/dtos";
import { LongTextTooltip } from '../long-text-tooltip';
import { MarkdownText } from '../markdown-text';
import { ModuleTypeIcon } from '../module-type-icon';

export function CourseViewPage() {
    const ctx: CourseManagementDataContextValue | null = useContext<CourseManagementDataContextValue | null>(CourseManagementDataContext);
    const { selectedCourseFull, selectedCourseLoading, selectedCourseError} = ctx!;

    if (selectedCourseLoading) {
        return (
            <div className="p-4">
                <i className="pi pi-spin pi-spinner" />Loading course details…
            </div>
        );
    }

    if (selectedCourseError) {
        return (
            <div className="p-4 text-red-600">
                <i className="pi pi-exclamation-triangle" /> {selectedCourseError}
            </div>
        );
    }

    if (!selectedCourseFull) {
        return (
            <div className="p-4 text-gray-500">
                Select a course from the list to view its details.
            </div>
        );
    }

    const course = selectedCourseFull;

    return (
        <div className="course-view-page p-4 flex flex-column gap-4" style={{ overflowY: "auto", height: "100%" }}>
            <LongTextTooltip target=".module-tooltip" position="left" />

            {/* Title */}
            <div>
                <h2 className="m-0">{course.name}</h2>
                <p className="font-semibold">{formatCoursePrice(course.price)}</p>
                <p>{course.certificate_enabled === false ? 'No certificate on completion' : 'Certificate on completion'}</p>
                <MarkdownText className="text-gray-600 m-0" text={course.description} />
            </div>

            <Divider />

            {/* Modules */}
            <Panel header="Modules">
                {course.modules.length === 0 && (
                    <div className="text-gray-500">No modules assigned.</div>
                )}

                {course.modules.length > 0 && (
                    <ol className="m-0 pl-3">
                        {course.modules
                            .sort((a, b) => a.position - b.position)
                            .map(cm => {
                                const full = ctx!.allModules.find(m => m.module_key === cm.module_key);
                                return (<>
                                     <li
                                        key={cm.module_key}
                                        className="cursor-help module-tooltip"
                                        data-markdown-tooltip={full?.description}
                                        tabIndex={0}
                                    >
                                        <strong className="inline-flex align-items-center gap-2">{full && <ModuleTypeIcon managed={full.is_managed} />}{full?.name ?? cm.module_key}</strong>
                                    </li>
                                </>);
                            })}
                    </ol>
                )}
            </Panel>

            <Panel header="Course prerequisites">
                {course.course_prerequisites?.length === 0 && (
                    <div className="text-gray-500">No prerequisite courses.</div>
                )}

                {course.course_prerequisites?.length > 0 && (
                    <ul className="m-0 pl-3">
                        {course.course_prerequisites.map((p, idx) => {
                            const prereqCourse = ctx!.allCourses.find(c => c.course_key === p.course_key);

                            return (
                                <li key={idx}>
                                    <strong>{prereqCourse?.name ?? p.course_key}</strong>

                                    {p.is_optional
                                        ? <Tag value="optional" severity="warning" className="ml-2" />
                                        : <Tag value="required" severity="info" className="ml-2" />
                                    }
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Panel>

            <Panel header="Module dependencies">
                {course.modules.length === 0 && (
                    <div className="text-gray-500">No modules assigned.</div>
                )}

                {course.modules.length > 0 && (
                    <ul className="m-0 pl-3">
                        {course.modules.map(cm => {
                            const moduleFull = ctx!.allModules.find(m => m.module_key === cm.module_key);

                            return (
                                <li key={cm.module_key} className="mb-2">
                                    <strong className="inline-flex align-items-center gap-2">{moduleFull && <ModuleTypeIcon managed={moduleFull.is_managed} />}{moduleFull?.name ?? cm.module_key}</strong>

                                    {(cm.prerequisites?.length ?? 0) === 0 && (
                                        <span className="text-gray-500 ml-2">(no prerequisites)</span>
                                    )}

                                    {(cm.prerequisites?.length ?? 0) > 0 && (
                                        <ul className="mt-1 pl-3">
                                            {cm.prerequisites.map((p, idx) => {
                                                const prereqFull = ctx!.allModules.find(
                                                    m => m.module_key === p.module_key
                                                );

                                                return (
                                                    <li key={idx}>
                                                        depends on{" "}
                                                        <strong className="inline-flex align-items-center gap-2">{prereqFull && <ModuleTypeIcon managed={prereqFull.is_managed} />}{prereqFull?.name ?? p.module_key}</strong>

                                                        {p.is_optional
                                                            ? <Tag value="optional" severity="warning" className="ml-2" />
                                                            : <Tag value="required" severity="info" className="ml-2" />
                                                        }
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Panel>

            <Panel header="Answer comparisons">
                <CourseAnswerComparisons course={course} modules={ctx!.allModules} />
            </Panel>

            <Panel header="Assigned users">
                {course.users?.length === 0 && (
                    <div className="text-gray-500">No users assigned.</div>
                )}

                {course.users?.length > 0 && (
                    <ul className="m-0 pl-3">
                        {course.users.map((cu, idx) => {
                            const user: UserDto | undefined = ctx!.allUsers.find(u => u.sub === cu.sub);
                            return (
                                <li key={idx}>
                                    <strong>{formatUserName(user!, true)}</strong>

                                    <span className="text-gray-500 ml-2">
                                        assigned at {cu.assigned_at}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Panel>
        </div>
    );
}
